import { openModal } from '@/lib/modal';
import { createContact, updateContact, removeContactFromProject, deleteContact } from '@/lib/data/contacts';
import { TRADE_LABEL } from '@/lib/labels';
import { emptyToNull } from '@/lib/form-values';
import { deleteContactWarning, NO_USAGE, type ContactUsage } from '@/lib/contacts-library';
import type { DesignerContactRow, Trade } from '@/types/database';

/**
 * Shared contact create/edit modal, on the same two-mode shape as
 * `schedule-modal.ts` and `punch-modal.ts`.
 *
 * The modes differ in ONE thing, and it is the dangerous one — what Delete
 * means:
 *
 *   project mode (`projectId` given) — Delete removes them from THAT project
 *     and leaves them in the firm's contacts. A new contact is also added to
 *     the project.
 *   library mode (no `projectId`)    — Delete removes the person entirely,
 *     which cascades across every project. It asks first, with the counts.
 *
 * Before this was shared, only the project version existed; the contacts page
 * needed the other half, and two copies of a nine-field form was the wrong way
 * to get it.
 */
export function openContactModal(opts: {
  /** Omit for the firm-wide contacts page. */
  projectId?: string;
  contact?: DesignerContactRow;
  /** Library mode only: what the delete would take with it. */
  usage?: ContactUsage;
  onDone: () => void;
}): void {
  const c = opts.contact;
  const inProject = !!opts.projectId;

  openModal({
    title: c ? 'Edit contact' : 'Add contact',
    showDelete: !!c,
    // Never just "Delete": in project mode the button does not delete the
    // person, and in library mode it deletes them from every project. The
    // question has to say which one is about to happen — openModal asks it,
    // so nothing here asks a second time.
    deleteLabel: inProject ? 'Remove from project' : 'Delete everywhere',
    deleteConfirm: c
      ? (inProject
        ? `Remove ${c.name} from this project?\n\nThey stay in your firm’s contacts, and on every other project they’re on.`
        : deleteContactWarning(c, opts.usage ?? NO_USAGE))
      : undefined,
    fields: [
      { name: 'name', label: 'Name', required: true, defaultValue: c?.name },
      { name: 'role', label: 'Role', defaultValue: c?.role },
      { name: 'company', label: 'Company', defaultValue: c?.company },
      { name: 'email', label: 'Email', type: 'email', defaultValue: c?.email },
      { name: 'phone', label: 'Phone', type: 'tel', defaultValue: c?.phone },
      // Four independent flags (decisions log #3) — one person can be more
      // than one of these.
      { name: 'is_client', label: 'Client', type: 'checkbox', defaultValue: c?.is_client ?? false },
      { name: 'is_contractor', label: 'Contractor', type: 'checkbox', defaultValue: c?.is_contractor ?? false },
      { name: 'is_subcontractor', label: 'Subcontractor', type: 'checkbox', defaultValue: c?.is_subcontractor ?? false },
      { name: 'is_vendor', label: 'Vendor', type: 'checkbox', defaultValue: c?.is_vendor ?? false },
      { name: 'trade', label: 'Trade (if subcontractor)', type: 'select', defaultValue: c?.trade ?? '', options: [
        { value: '', label: '— None —' },
        ...(Object.keys(TRADE_LABEL) as Trade[]).map((k) => ({ value: k, label: TRADE_LABEL[k] })),
      ] },
    ],
    onSubmit: async (v) => {
      const tradeVal = String(v.trade);
      const payload = {
        name: String(v.name),
        role: emptyToNull(v.role),
        company: emptyToNull(v.company),
        email: emptyToNull(v.email),
        phone: emptyToNull(v.phone),
        is_client: !!v.is_client,
        is_contractor: !!v.is_contractor,
        is_subcontractor: !!v.is_subcontractor,
        is_vendor: !!v.is_vendor,
        trade: (tradeVal === '' ? null : tradeVal as Trade),
      };
      if (c) await updateContact(c.id, payload);
      else await createContact(payload, opts.projectId);
      opts.onDone();
    },
    onDelete: c
      ? async () => {
        if (opts.projectId) {
          // Removes them from THIS project, leaving them in the library.
          // Deleting the person outright would cascade to their assignments
          // and portal links on every other project, which is not what
          // "remove from this project" should mean.
          await removeContactFromProject(opts.projectId, c.id);
        } else {
          await deleteContact(c.id);
        }
        opts.onDone();
      }
      : undefined,
  });
}
